import React, { useEffect, useState } from "react";
import { useOrganization, UserButton } from "@clerk/clerk-react";
import { useSidebar } from "./useSidebar";

import ToolTipWrapper from "components/common/helper/tooltipWrapper";
import {
  ShieldCheck,
  ChevronsLeft,
  ChevronsRight,
  Search,
  Send,
  ZoomIn,
  AtSign,
  ZoomInIcon,
  MailOpen,
  BriefcaseBusinessIcon,
  History,
  CreditCard,
  UsersRound,
  Bell,
  MessageSquare,
  Settings,
  Building2,
  GroupIcon,
} from "lucide-react";
import { Button } from "components/shadcn/ui/button";
import { Progress } from "components/shadcn/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/shadcn/ui/tooltip";
import { FEATURE_HIGHLIGHT_NAME } from "components/campaign/types";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/shadcn/ui/popover";
import NotificationPopoverContent from "./notifications/notifications";
import { useSelector } from "react-redux";
import { selectNotificationCount } from "./notifications/notificationSlice";
import ConfirmDialog from "components/common/helper/confirmDialogue";
import { selectUnreadMessageCount } from "../../components/messaging-thread/messagingThreadSlice";
import { ORGANIZATION_ROLE } from "./types";
import { SUBSCRIPTION_DISPLAY_NAME } from "components/subscription/types";
import { Badge } from "components/shadcn/ui/badge";

const iconMap: Record<string, React.ReactNode> = {
  Search: <Search className="w-5 h-5" />,
  History: <History className="w-5 h-5" />,
  ZoomIn: <ZoomIn className="w-5 h-5" />,
  Send: <Send className="w-5 h-5" />,
  SvgDealTracker: <BriefcaseBusinessIcon className="w-5 h-5" />,
  ShieldCheck: <ShieldCheck className="w-5 h-5" />,
  UsersRound: <UsersRound className="w-5 h-5" />,
};

interface SidebarProps {
  loggedIn: boolean;
  user: any;
  openNotifications: boolean;
  setOpenNotifications: (val: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  loggedIn,
  user,
  openNotifications,
  setOpenNotifications,
}) => {
  const {
    collapsed,
    setCollapsed,
    selected,
    navItems,
    handleNavigation,
    isDisabled,
    renderProgressBars,
    theme,
    currentSubscription,
    renderCustomFeatureName,
    communityInvitationState,
    setCommunityInvitationState,
    handleJoinCommunity,
  } = useSidebar(user, loggedIn);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const { organization } = useOrganization();

  const unreadNotificationCount = useSelector(selectNotificationCount);
  const unreadMessageCount = useSelector(selectUnreadMessageCount);

  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const sidebar = document.getElementById("sidebar");
      if (!sidebar) return;

      const rect = sidebar.getBoundingClientRect();
      const overSidebar =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      setIsHovered(overSidebar);
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    setCollapsed(!isHovered);
  }, [isHovered, setCollapsed]);

  const ButtonWithOptionalTooltip = ({
    children,
    content,
    showTooltip,
  }: any) => {
    return showTooltip ? (
      <ToolTipWrapper content={content}>{children}</ToolTipWrapper>
    ) : (
      children
    );
  };

  return (
    <div className="flex flex-row">
      <ConfirmDialog
        title={"Accept Community Invitation"}
        description={communityInvitationState.message}
        confirmLabel={"Join Community"}
        open={communityInvitationState.showModal}
        showCancelButton
        onConfirm={handleJoinCommunity}
        onOpenChange={() =>
          setCommunityInvitationState({
            showModal: false,
            message: "",
          })
        }
      />

      <div
        className={`h-screen flex flex-row bg-white border-r transition-all duration-300 ${
          collapsed ? "w-[70px]" : "w-60"
        }`}
      >
        <div className="flex flex-col w-full">
          <div className="relative flex items-center justify-center px-3 py-4 border-b bg-white shrink-0">
            <img
              src={
                theme === "bizcrunch"
                  ? collapsed
                    ? "/assets/bcLetterLogo.png"
                    : "/assets/bcLogo.png"
                  : theme === "harbour"
                  ? collapsed
                    ? "/assets/hcLetterLogo.png"
                    : "/assets/logoHC.png"
                  : collapsed
                  ? "/assets/logoLetter.png"
                  : "/assets/logoB4B.jpg"
              }
              alt="Logo"
              className="h-8 object-contain transition-all duration-300"
            />

            <div
              className="absolute top-1/2 -right-5 -translate-y-1/2 z-50 transition-all duration-300 hover:-right-6"
              style={{ pointerEvents: "auto" }}
            >
              <Button
                variant="secondary"
                size="icon"
                className="rounded-full bg-white border shadow-md hover:bg-gray-50 w-8 h-8"
                onClick={() => setCollapsed(!collapsed)}
              >
                {collapsed ? (
                  <ChevronsRight className="w-4 h-4 text-gray-700" />
                ) : (
                  <ChevronsLeft className="w-4 h-4 text-gray-700" />
                )}
              </Button>
            </div>
          </div>

          <div
            id="sidebar"
            className="flex flex-col flex-1 overflow-y-auto justify-between w-full"
          >
            <div className="flex flex-col gap-3 p-3 w-full">
              {navItems
                .filter((item) => item.visible)
                .map((item) => {
                  const active = selected === item.key;
                  return (
                    <ButtonWithOptionalTooltip
                      key={item.key}
                      content={item.label}
                      showTooltip={collapsed}
                    >
                      <Button
                        variant={active ? "primary" : "secondary"}
                        disabled={isDisabled}
                        className={`w-full flex items-center transition-all duration-300 ${
                          collapsed ? "justify-center" : "!justify-start"
                        } ${
                          active ? "bg-[#FFF1F3] font-semibold" : "bg-gray-100"
                        }`}
                        onClick={() =>
                          !isDisabled &&
                          handleNavigation(
                            item.label,
                            item.path,
                            item.resetState
                          )
                        }
                      >
                        {iconMap[item.icon]}
                        {!collapsed && (
                          <span className="ml-3">{item.label}</span>
                        )}
                      </Button>
                    </ButtonWithOptionalTooltip>
                  );
                })}
            </div>

            <div className="border-t p-4 flex flex-col gap-2 bg-white shrink-0 !items-center w-full">
              <div
                className={`flex ${
                  !collapsed
                    ? "w-full flex-row justify-around"
                    : "flex-col gap-2 "
                }`}
              >
                <Popover
                  open={openNotifications}
                  onOpenChange={(val) => setOpenNotifications(val)}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size={collapsed ? "icon" : "default"}
                      className={`relative flex items-center justify-center ${
                        !collapsed ? "!bg-gray-100" : ""
                      }`}
                    >
                      <Bell className="w-5 h-5" />

                      {unreadNotificationCount > 0 && (
                        <span className="absolute -top-2 -right-3 h-6 w-6 flex items-center justify-center bg-red-500 text-white rounded-full text-[10px] font-extrabold leading-none overflow-hidden">
                          {unreadNotificationCount > 99
                            ? "99+"
                            : unreadNotificationCount}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="right"
                    align="end"
                    sideOffset={5}
                    alignOffset={20}
                    className="w-full z-[999] rounded-[24px] bg-gradient-to-r from-white to-gray-100 p-4 shadow-lg border border-gray-200 ring-1 ring-gray-300 hover:scale-105 transition-transform duration-200"
                  >
                    <NotificationPopoverContent
                      onOpen={openNotifications}
                      onClose={() => setOpenNotifications(false)}
                    />
                  </PopoverContent>
                </Popover>

                <Button
                  variant="outline"
                  size={collapsed ? "icon" : "default"}
                  className={`relative flex items-center justify-center ${
                    !collapsed ? "!bg-gray-100" : ""
                  }`}
                  onClick={() => handleNavigation("threads", "/threads", true)}
                >
                  <MessageSquare className="w-5 h-5" />

                  {unreadMessageCount > 0 && (
                    <span className="absolute -top-2 -right-3 h-6 w-6 flex items-center justify-center bg-red-500 text-white rounded-full text-[10px] font-extrabold leading-none overflow-hidden">
                      {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                    </span>
                  )}
                </Button>
              </div>

              {currentSubscription &&
                currentSubscription.status !== "CANCELLED" &&
                renderProgressBars()?.map((quota) => {
                  if (collapsed) {
                    const radius = 20;
                    const strokeWidth = 3;
                    const normalizedRadius = radius - strokeWidth * 2;
                    const circumference = normalizedRadius * 2 * Math.PI;
                    const progress = quota.progress ?? 0;
                    const strokeDashoffset =
                      circumference - (progress / 100) * circumference;

                    return (
                      <TooltipProvider key={quota.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="relative flex flex-col items-center justify-center">
                              <svg
                                height={radius * 2}
                                width={radius * 2}
                                className="rotate-[-90deg]"
                              >
                                <circle
                                  stroke="#E5E7EB"
                                  fill="transparent"
                                  strokeWidth={strokeWidth}
                                  r={normalizedRadius}
                                  cx={radius}
                                  cy={radius}
                                />
                                <circle
                                  stroke="#A11043"
                                  fill="transparent"
                                  strokeWidth={strokeWidth}
                                  strokeLinecap="round"
                                  strokeDasharray={`${circumference} ${circumference}`}
                                  strokeDashoffset={strokeDashoffset}
                                  r={normalizedRadius}
                                  cx={radius}
                                  cy={radius}
                                  style={{
                                    transition: "stroke-dashoffset 0.5s ease",
                                  }}
                                />
                              </svg>
                              <span className="absolute text-[10px] font-semibold text-gray-700">
                                {quota.featureName ===
                                FEATURE_HIGHLIGHT_NAME.EMAIL_OUTREACH ? (
                                  <AtSign className="h-4 w-4 text-gray-600" />
                                ) : quota.featureName ===
                                  FEATURE_HIGHLIGHT_NAME.LETTER_OUTREACH ? (
                                  <MailOpen className="w-4 h-4 text-gray-600" />
                                ) : quota.featureName ===
                                  FEATURE_HIGHLIGHT_NAME.COMPANY_COLLECTIONS ? (
                                  <ZoomInIcon className="h-4 w-4 text-gray-600" />
                                ) : null}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="z-9999">
                            <p className="text-xs font-medium">
                              {`${renderCustomFeatureName(
                                quota?.featureName || ""
                              )} ${
                                currentSubscription.status === "TRAILING"
                                  ? " (Trial)"
                                  : ""
                              }`}
                              <span className="font-semibold">
                                {" "}
                                {quota.usedQuota}/{quota.totalQuota}
                              </span>
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  }

                  return (
                    <div
                      key={quota.id}
                      className={`flex flex-col rounded-[8px] p-2 min-w-[180px] text-left w-full ${
                        collapsed ? "justify-center" : "!justify-start"
                      }`}
                    >
                      <span className="text-gray-700 text-xs font-semibold">
                        {`${renderCustomFeatureName(
                          quota?.featureName || ""
                        )} ${
                          currentSubscription.status === "TRAILING"
                            ? " (Trial)"
                            : ""
                        }`}
                        :
                        <span className="font-bold">
                          {" "}
                          {quota.usedQuota}/{quota.totalQuota}
                        </span>
                      </span>
                      <Progress
                        value={quota.progress}
                        className="mt-2 h-2 bg-green-200"
                        style={{ "--primary-800": "#A11043" } as any}
                      />
                    </div>
                  );
                })}
              <div>
                {currentSubscription.product?.displayName ===
                  SUBSCRIPTION_DISPLAY_NAME.TEAM &&
                  (!collapsed ? (
                    <ToolTipWrapper content={organization?.name}>
                      <Badge
                        variant="secondary"
                        className="mx-auto flex items-start gap-2 max-w-60 px-3 py-2 text-center border border-[#FECCD6]"
                      >
                        {organization?.imageUrl ? (
                          <img
                            src={organization.imageUrl}
                            alt={organization.name}
                            className="h-5 w-5 rounded-sm object-cover shrink-0 mt-0.5"
                          />
                        ) : (
                          <GroupIcon className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                        )}

                        <span className="text-sm font-medium min-w-0 line-clamp-2">
                          {organization?.name}
                        </span>
                      </Badge>
                    </ToolTipWrapper>
                  ) : (
                    <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-[32px] border border-input bg-background hover:bg-muted cursor-pointer">
                      {organization?.imageUrl ? (
                        <img
                          src={organization.imageUrl}
                          alt={organization.name}
                          className="h-5 w-5 rounded-sm object-cover"
                        />
                      ) : (
                        <GroupIcon className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  ))}
              </div>

              <div className="flex items-center gap-2">
                <ToolTipWrapper content="Manage Profile">
                  <UserButton
                    appearance={{
                      elements: {
                        avatarBox: "w-8 h-8",
                        userButtonPopoverCustomItemButton:
                          "font-normal text-gray-800 font-sans",
                      },
                    }}
                    showName={!collapsed}
                  >
                    {user?.organizationRole === ORGANIZATION_ROLE.ADMIN && (
                      <UserButton.MenuItems>
                        <UserButton.Action
                          label="Billing"
                          labelIcon={<CreditCard className="h-4 w-4" />}
                          onClick={() =>
                            handleNavigation("Billing", "/billing")
                          }
                        />
                      </UserButton.MenuItems>
                    )}

                    {user?.organizationRole === ORGANIZATION_ROLE.ADMIN && (
                      <UserButton.MenuItems>
                        <UserButton.Action
                          label="Platform Settings"
                          labelIcon={<Settings className="h-4 w-4" />}
                          onClick={() =>
                            handleNavigation("settings", "/platform-settings")
                          }
                        />
                      </UserButton.MenuItems>
                    )}

                    {user?.organizationRole === ORGANIZATION_ROLE.ADMIN &&
                      currentSubscription.product?.displayName ===
                        SUBSCRIPTION_DISPLAY_NAME.TEAM && (
                        <UserButton.MenuItems>
                          <UserButton.Action
                            label="Team Management"
                            labelIcon={<Building2 className="h-4 w-4" />}
                            onClick={() =>
                              handleNavigation(
                                "Team Management",
                                "/team-management"
                              )
                            }
                          />
                        </UserButton.MenuItems>
                      )}
                  </UserButton>
                </ToolTipWrapper>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
